import { Injectable } from '@angular/core';
import { HttpParams, HttpEventType, HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { Observable, Subject, BehaviorSubject, ReplaySubject, forkJoin, throwError, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Router, ActivatedRoute, NavigationStart } from '@angular/router';

import * as URLs from '../global.urls';
import { GlobalService } from '../services/global.service';
import { SessionService } from '../services/session.service';
import * as Classes from '../classes/models.classes';

//import { Http } from '@angular/http';

@Injectable({
    providedIn: 'any'
})
export class UserService {
    lcl_params: Classes.UserID_Params;
    //private readonly tokenKey = 'auth_token';
    private inactivityTimeout: any;

    private isAuthenticated = new BehaviorSubject<boolean>(false);

    isAuthenticated$ = this.isAuthenticated.asObservable();

    constructor(
        private globalService: GlobalService,
        private sessionService: SessionService,
        private http: HttpClient,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    login(username: string, password: string): Observable<any> {
        //return this.http.post<any>(URLs.UserUrls.CheckLogin, { username, password });
        return this.http.post<any>(URLs.UserUrls.CheckLogin, { username, password }).pipe(
            tap(response => {
                this.sessionService.setExpiration(response.expiration);
                const uid = response.userID ?? response.UserID;
                this.sessionService.setUserID(String(uid));
                this.sessionService.setUsername(response.username ?? response.Username);
                this.sessionService.setProfileName(response.profileName ?? response.ProfileName);
                this.sessionService.setUserImg(response.userImg ?? response.UserImg);
                this.sessionService.setUserBannerImg(response.userBannerImg ?? response.UserBannerImg);
            })
        );
    }

    googleLogin(body: { idToken: string; username?: string }): Observable<any> {
        return this.http.post<any>(URLs.UserUrls.GoogleLogin, body).pipe(
            tap(response => {
                const needsUsername = response?.needsUsername ?? response?.NeedsUsername;
                if (!response || needsUsername) {
                    return;
                }
                if (response.expiration != null) {
                    this.sessionService.setExpiration(response.expiration);
                    const uid = response.userID ?? response.UserID;
                    this.sessionService.setUserID(String(uid));
                    this.sessionService.setUsername(response.username ?? response.Username);
                    this.sessionService.setProfileName(response.profileName ?? response.ProfileName);
                    this.sessionService.setUserImg(response.userImg ?? response.UserImg);
                    this.sessionService.setUserBannerImg(response.userBannerImg ?? response.UserBannerImg);
                }
            })
        );
    }

    isUserNameAvailable(userName: string): Observable<{ available: boolean }> {
        const params = new HttpParams().set('userName', userName);
        return this.http.get<{ available: boolean }>(URLs.UserUrls.IsUserNameAvailable, { params });
    }

    checkAuthentication2(): Observable<boolean> {
        return this.http.get<Classes.AuthResponse>(URLs.UserUrls.CheckToken).pipe(
            map(response => response.authenticated), // Directly map to the `authenticated` boolean
            catchError(error => {
                console.error('Error checking authentication', error);
                return of(false); // Ensure it returns an Observable<boolean>
            })
        );
    }

    checkAuthentication(): Observable<boolean> {
        return this.http.get<boolean>(URLs.UserUrls.CheckToken, { observe: 'response' }).pipe(
            map((response) => response.status === 200 && response.body === true),
            catchError((error) => {
                if (error?.status === 401) {
                    return of(false);
                }
                console.warn('CheckToken request failed', error?.status, error?.message);
                return of(false);
            })
        );
    }


    registerUser(formData: any): Observable<any> {
        return this.http.post<any>(URLs.UserUrls.AddNewUser, formData);
    }

    logout(): void {
        this.http.post(URLs.UserUrls.Logout, {}).subscribe({
            next: () => {
                this.isAuthenticated.next(false);
                this.sessionService.clearSession();
                this.router.navigate(['/'], { relativeTo: this.route });
            },
            error: () => {
                this.isAuthenticated.next(false);
                this.sessionService.clearSession();
                this.router.navigate(['/'], { relativeTo: this.route });
            }
        });
    }

    getUserProfile(userName: string): Observable<Classes.ProfileModel> {
        const params = new HttpParams().set('userName', userName);
        return this.http.get<Classes.ProfileModel>(URLs.UserUrls.GetUserProfile, { params });
    }

    updateUserProfile(formData: FormData): Observable<any> {
        return this.http.post<any>(URLs.UserUrls.UpdateUserProfile, formData);
    }

    getFollowStatus(UserID: number, postUserId: number): Observable<{ isFollowed: boolean }> {
        if (postUserId == null) {
            console.error("Error: postUserId is undefined or null");
            return throwError(() => new Error("Invalid postUserId parameter"));
        }

        let params = new HttpParams().set('UserID', UserID.toString());
        params = params.set('postUserId', postUserId.toString());
        return this.http.get<{ isFollowed: boolean }>(URLs.UserUrls.GetFollowStatus, { params });
    }

    getMutedStatus(UserID: number, postUserId: number): Observable<{ isMuted: boolean }> {
        let params = new HttpParams().set('UserID', UserID.toString());
        params = params.set('postUserId', postUserId.toString());
        return this.http.get<{ isMuted: boolean }>(URLs.UserUrls.GetMutedStatus, { params });
    }

    getUserStatuses(UserID: number, postUserId: number): Observable<Classes.UserStatuses> {
        const params = new HttpParams()
            .set('UserID', UserID.toString())
            .set('postUserId', postUserId.toString());
        return this.http.get<Classes.UserStatuses>(URLs.UserUrls.GetUserStatuses, { params });
    }


    followUser(UserID: number, postUserId: number, isFollowed: boolean, isMuted: boolean, isBlocked: boolean): Observable<any> {
        const body = { UserID, postUserId, isFollowed, isMuted, isBlocked }; // Preparing the body data 
        return this.http.post(URLs.UserUrls.FollowUser, body);
    }

    muteUser(UserID: number, postUserId: number, isFollowed: boolean, isMuted: boolean, isBlocked: boolean): Observable<any> {
        const body = { UserID, postUserId, isFollowed, isMuted, isBlocked }; // Preparing the body data 
        return this.http.post(URLs.UserUrls.MuteUser, body);
    }

    blockUser(UserID: number, postUserId: number, isFollowed: boolean, isMuted: boolean, isBlocked: boolean): Observable<any> {
        const body = { UserID, postUserId, isFollowed, isMuted, isBlocked }; // Preparing the body data 
        return this.http.post(URLs.UserUrls.BlockUser, body);
    }

    deletePost(postId: number): Observable<any> {
        const params = new HttpParams().set('postID', postId.toString());
        return this.http.post(URLs.UserUrls.DeletePost, null, { params });
    }


    pinPost(postId: number): Observable<any> {
        return this.http.post(URLs.UserUrls.PinPost, { postId });
    }

    highlightPost(postId: number): Observable<any> {
        return this.http.post(URLs.UserUrls.HighlightPost, { postId });
    }


    // Method to start the inactivity timeout
    startInactivityTimeout(timeout: number): void {
        clearTimeout(this.inactivityTimeout);
        this.inactivityTimeout = setTimeout(() => {
            // Expire token or perform other actions on timeout
            this.expireToken();
        }, timeout);
    }

    // Method to reset the inactivity timeout
    resetInactivityTimeout(timeout: number): void {
        this.startInactivityTimeout(timeout);
    }

    // Method to expire the token
    expireToken(): void {
        this.logout();
    }

    // Method to handle user activity
    handleUserActivity(timeout: number): void {
        this.resetInactivityTimeout(timeout);
    }

    getToken(): string | null {
        // Retrieve JWT token from local storage
        return localStorage.getItem(GlobalService.tokenName);
    }

    isTokenExpired(): boolean {
        const token = this.getToken();
        if (!token) {
            // Token not found or expired
            return true;
        }

        // Extract expiration time from JWT token payload
        const expirationTime = this.getExpirationTime(token);

        // Calculate remaining time until expiration
        const currentTime = Math.floor(Date.now() / 1000); // Convert milliseconds to seconds
        const expiresIn = expirationTime - currentTime;

        // Return true if token has expired, false otherwise
        return expiresIn <= 0;
    }

    private getExpirationTime(token: string): number {
        // Extract expiration time (exp) from JWT token payload
        const payload = this.parseJwt(token);
        return payload ? payload.exp : 0;
    }

    private parseJwt(token: string): any {
        // Parse JWT token payload (base64url-encoded JSON)
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            return null;
        }
    }

}
